import React from 'react';
import cn from 'classnames';
import { Link } from 'react-router-dom';
import moment from 'moment';

import { DATE_TIME_FORMAT } from '../../../constants';
import { SubNav } from '../../SubNav/quick';
import UserInfo from '../../../containers/UserInfo';
import { getSubStatus, quickRenderPlan } from 'helpers/subs';
import EditableRow from 'components/EditableRow';
import { prettyKey } from 'helpers/formatters';
import ToggleLockedPlan from 'components/ToggleLockedPlan/quick';
import ServiceAddress from '../ServiceAddress';
import { can, permActions } from '../../../permissions';

export default ({
  sub,
  subId,
  userId,
  isAdmin,
  contracts,
  handleUpdateField,
  subServiceCodes,
  runningActions,
  loggedInUser,
}) => {
  const subStatus = getSubStatus(sub);
  const upcomingPlanToDisplay =
    sub.upcoming_plan || (sub.quick_status === 'active' && sub.plan);
  const isExpired = subStatus === 'expired';
  const showSubPurchases = can(loggedInUser, permActions.VIEW_PURCHASES);
  const showPrices = can(loggedInUser, permActions.VIEW_PRICES);
  return (
    <div className="Sub">
      <div className="flex-justify-space-between">
        <h1>Quick Subscription details</h1>
        {showSubPurchases && (
          <div>
            <Link
              to={`/users/${userId}/purchases?subscription=${subId}&subCarrier=quick`}
            >
              Subscription purchases
            </Link>
          </div>
        )}
      </div>
      <SubNav sub={sub} isBackToSub={false} isPortInLinks showNetwork />
      <table className="table">
        <tbody>
          <tr>
            <td>ID</td>
            <td>{sub.id}</td>
            <td></td>
          </tr>
          <tr>
            <td>User Profile ID</td>
            <td>{sub.user_profile}</td>
            <td></td>
          </tr>
          <EditableRow
            name="first_name"
            label={prettyKey('first_name')}
            value={sub.first_name}
            type="input"
            save={handleUpdateField}
            thirdRowWidth="255px"
          />
          <EditableRow
            name="last_name"
            label={prettyKey('last_name')}
            value={sub.last_name}
            type="input"
            save={handleUpdateField}
            thirdRowWidth="255px"
          />
          <tr>
            <td>MDN</td>
            <td>{sub.mdn}</td>
            <td></td>
          </tr>
          <tr>
            <td>Device ID</td>
            <td>{sub.device_id}</td>
            <td></td>
          </tr>
          <tr>
            <td>ICC ID</td>
            <td>{sub.icc_id}</td>
            <td></td>
          </tr>
          <tr>
            <td>Vendor</td>
            <td>{sub.vendor}</td>
            <td></td>
          </tr>
          <tr>
            <td>Subscription Type</td>
            <td>{sub.subscription_type}</td>
            <td></td>
          </tr>
          <tr>
            <td>Quick Status</td>
            <td>{subStatus}</td>
            <td></td>
          </tr>
          {Boolean(runningActions?.length) && (
            <tr className="red-text">
              <td>Running Actions</td>
              <td colSpan="2">
                {runningActions.map(({ action, created_at }, i) => (
                  <React.Fragment key={i}>
                    {action && <span>Action type: {action} | </span>}
                    {created_at && (
                      <span>
                        Start date:{' '}
                        {moment(created_at).format(DATE_TIME_FORMAT)}
                      </span>
                    )}
                    <br />
                  </React.Fragment>
                ))}
              </td>
            </tr>
          )}
          <tr>
            <td>Effective Date</td>
            <td>
              {sub.effective_date
                ? moment(sub.effective_date).format(DATE_TIME_FORMAT)
                : sub.effective_date}
            </td>
            <td></td>
          </tr>
          <tr>
            <td>Expiry Date</td>
            <td>
              {sub.expiry_date
                ? moment(sub.expiry_date).format(DATE_TIME_FORMAT)
                : sub.expiry_date}
            </td>
            <td></td>
          </tr>
          <tr>
            <td>Expiry Type</td>
            <td>{sub.expiry_type}</td>
            <td></td>
          </tr>
          <tr>
            <td>PIN</td>
            <td>{sub.pin}</td>
            <td></td>
          </tr>
          <tr>
            <td>Activation Type</td>
            <td>{sub.activation_type}</td>
            <td></td>
          </tr>
          <tr>
            <td>Suspension Type</td>
            <td>{sub.suspension_type}</td>
            <td></td>
          </tr>
          <tr>
            <td>
              Plan{' '}
              {sub.plan_locked && <span className="text-danger">(Locked)</span>}
            </td>
            <td>
              <span>{quickRenderPlan(sub?.plan, showPrices)}</span>
            </td>
            <td>{!isExpired && <ToggleLockedPlan sub={sub} />}</td>
          </tr>
          <tr>
            <td>Upcoming Plan</td>
            <td>
              <span>{quickRenderPlan(upcomingPlanToDisplay, showPrices)}</span>
            </td>
            <td></td>
          </tr>
          <tr>
            <td>Renew At</td>
            <td>
              {sub.renew_at
                ? moment(sub.renew_at).format(DATE_TIME_FORMAT)
                : sub.renew_at}
            </td>
            <td></td>
          </tr>
          <tr>
            <td>eSIM Compatible</td>
            <td>{sub.esim_compatible ? 'Yes' : 'No'}</td>
            <td></td>
          </tr>
          <tr>
            <td>PUK 1</td>
            <td>{sub.puk1}</td>
            <td></td>
          </tr>
          <tr>
            <td>PUK 2</td>
            <td>{sub.puk2}</td>
            <td></td>
          </tr>
          <tr>
            <td>Agent ID</td>
            <td>{sub.agent_id}</td>
            <td></td>
          </tr>
          <tr>
            <td>Agent Name</td>
            <td>{sub.agent_name}</td>
            <td></td>
          </tr>
          <tr>
            <td>Device Status</td>
            <td>{sub.device_status}</td>
            <td></td>
          </tr>
          <tr>
            <td className={cn({ 'text-danger': sub.deleted })}>Deleted</td>
            <td className={cn({ 'text-danger': sub.deleted })}>
              {String(sub.deleted)}
            </td>
            <td></td>
          </tr>
          <tr>
            <td>Service Codes</td>
            <td>{subServiceCodes?.join(', ')}</td>
            <td></td>
          </tr>
          <tr>
            <td>Device Protection Contracts</td>
            <td>
              {contracts &&
                contracts.map((contract) => {
                  return (
                    <Link
                      className="mar-r"
                      key={contract.id}
                      to={`/device-protection/contracts/${contract.id}/view`}
                    >
                      ID: {contract.id}
                    </Link>
                  );
                })}
            </td>
            <td>
              {!isExpired && (
                <Link
                  to={`/quick/subscriptions/${subId}/contracts/create-contract`}
                >
                  Create New Contract
                </Link>
              )}
            </td>
          </tr>
          <tr>
            <td>Created By</td>
            <td>
              <UserInfo userId={sub.created_by} type="nameIdRole" />
            </td>
            <td></td>
          </tr>
          <tr>
            <td>SMS Notifications</td>
            <td>{String(sub.sms_notifications)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>
      {sub?.service_address && (
        <React.Fragment>
          <h3>Service Address</h3>
          <ServiceAddress serviceAddress={sub.service_address} />
        </React.Fragment>
      )}
    </div>
  );
};
